import { X } from 'lucide-react';
import type { CSSProperties } from 'react';
import { getApiErrorMessage } from '../../lib/api';
import type { DashboardStoreDetail } from '../../types/domain';
import { contrastTextColour, productTreemapColours } from './dashboardChartColours';

const dateFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Asia/Karachi',
});

function formatDate(value: string) {
  return dateFormatter.format(new Date(`${value}T12:00:00+05:00`));
}

function targetValue(value: string, unit: string) {
  const formatted = Number(value).toLocaleString('en-PK', { maximumFractionDigits: 3 });
  return unit === 'PKR' ? `PKR ${formatted}` : `${formatted} ${unit}`;
}

export function StoreSalesDetailModal({
  detail,
  error,
  isLoading,
  onClose,
}: {
  detail: DashboardStoreDetail | undefined;
  error: unknown;
  isLoading: boolean;
  onClose: () => void;
}) {
  const maximumUnits = Math.max(
    0,
    ...(detail?.products.flatMap((product) => product.daily_sales.map((cell) => cell.accepted_units)) ?? []),
  );
  const heatmapColours = detail ? productTreemapColours(detail.products) : [];
  const heatmapProducts = detail?.products.map((product, index) => ({
    product,
    colour: heatmapColours[index] ?? '#2f8065',
  })) ?? [];

  return (
    <div className="confirmation-backdrop store-sales-detail-backdrop" role="presentation">
      <section aria-labelledby="store-sales-detail-title" aria-modal="true" className="confirmation-dialog store-sales-detail-dialog" role="dialog">
        <button aria-label="Close store sales detail" className="icon-button confirmation-close" onClick={onClose} type="button">
          <X size={19} />
        </button>
        {isLoading ? (
          <div aria-live="polite" className="store-sales-detail-loading">
            <span className="button-spinner" /> Loading store sales detail…
          </div>
        ) : error ? (
          <div className="store-sales-detail-loading">
            <p className="form-error" role="alert">{getApiErrorMessage(error)}</p>
          </div>
        ) : detail ? (
          <>
            <header className="store-sales-detail-heading">
              <div>
                <p className="eyebrow">Store detail</p>
                <h2 id="store-sales-detail-title">{detail.name}</h2>
                <p>{detail.city}</p>
                <div className="store-sales-detail-target">
                  <span>Target achieved</span>
                  {detail.target_attainment.target_count ? (
                    <>
                      <strong>{Number(detail.target_attainment.percentage).toFixed(1)}%</strong>
                      <div>
                        {detail.target_attainment.summaries.map((summary) => (
                          <small key={summary.metric}>
                            {targetValue(summary.achieved_value, summary.display_unit)} / {targetValue(summary.target_value, summary.display_unit)}
                          </small>
                        ))}
                      </div>
                    </>
                  ) : <small>No target is configured for this store.</small>}
                </div>
              </div>
              <aside aria-label="Sales contributors" className="store-sales-detail-contributors">
                <span>Sales BAs</span>
                {detail.contributors.length ? (
                  <div>
                    {detail.contributors.map((contributor) => (
                      <span key={contributor.id} title={`${contributor.accepted_units.toLocaleString('en-PK')} accepted units`}>
                        {contributor.name}
                      </span>
                    ))}
                  </div>
                ) : <small>No accepted sales in this period</small>}
              </aside>
            </header>

            <section aria-labelledby="store-sales-heatmap-title" className="store-sales-detail-heatmap-section">
              <div>
                <h3 id="store-sales-heatmap-title">Daily product sales</h3>
                <p>Accepted units for the selected reporting period.</p>
              </div>
              {detail.products.length ? (
                <div className="store-sales-detail-heatmap-scroll">
                  <table className="store-sales-detail-heatmap">
                    <thead>
                      <tr>
                        <th scope="col">Product</th>
                        {detail.dates.map((date) => <th key={date} scope="col">{formatDate(date)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmapProducts.map(({ product, colour }) => {
                        const salesByDate = new Map(product.daily_sales.map((cell) => [cell.date, cell]));
                        return (
                          <tr key={product.id}>
                            <th scope="row">
                              <strong>{product.name}</strong>
                              <small>
                                {product.product_theme_color || product.brand_theme_color ? <i style={{ backgroundColor: product.product_theme_color || product.brand_theme_color || undefined }} /> : null}
                                {product.brand_name} · {product.sku}
                              </small>
                            </th>
                            {detail.dates.map((date) => {
                              const cell = salesByDate.get(date);
                              const units = cell?.accepted_units ?? 0;
                              const intensity = maximumUnits ? units / maximumUnits : 0;
                              return (
                                <td key={date}>
                                  <span
                                    aria-label={`${product.name} on ${formatDate(date)}: ${units.toLocaleString('en-PK')} accepted units`}
                                    className="store-sales-detail-heatmap-cell"
                                    style={{
                                      '--heat-share': `${12 + intensity * 76}%`,
                                      '--heat-colour': colour,
                                      color: intensity >= 0.78 ? contrastTextColour(colour) : 'var(--ink)',
                                    } as CSSProperties}
                                    title={cell
                                      ? `${units.toLocaleString('en-PK')} accepted units · PKR ${Number(cell.accepted_value).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`
                                      : 'No accepted sales'}
                                  >
                                    {units ? units.toLocaleString('en-PK') : '—'}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state compact store-sales-detail-empty">
                  <h3>No campaign products in this scope</h3>
                  <p>Adjust the filters or add products to the selected campaign.</p>
                </div>
              )}
            </section>
          </>
        ) : null}
      </section>
    </div>
  );
}
